import React, { useState, useEffect } from 'react';
import { Box, Typography, IconButton, Chip, Tooltip } from '@mui/material';
import { DataGrid, GridToolbar } from '@mui/x-data-grid';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import VisibilityIcon from '@mui/icons-material/Visibility';
import api from '../../api';

const ScreenedDocumentsTab = ({ activeMenu, showMsg }) => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const baseUrl = api.defaults.baseURL || `http://${window.location.hostname}:8000/api/v1`;

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        if (activeMenu === 'pan_card') {
          const res = await api.get('/screened/pan');
          setData(res.data || []);
        } else if (activeMenu === 'aadhaar_card') {
          const res = await api.get('/screened/aadhaar');
          setData(res.data || []);
        }
      } catch (err) { showMsg("Failed to load screened documents", "error"); }
      setLoading(false);
    };
    fetchData();
  }, [activeMenu, showMsg]);

  const renderStackedCell = (primary, secondary) => (
    <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', height: '100%' }}>
      <Typography variant="body2" sx={{ fontWeight: 600, lineHeight: 1.2 }}>{primary || '--'}</Typography>
      <Typography variant="caption" color="text.secondary">{secondary || '--'}</Typography>
    </Box>
  );

  const renderCenterCell = (content) => <Box sx={{ display: 'flex', alignItems: 'center', height: '100%' }}>{content}</Box>;

  // DEDICATED COLUMN SYSTEM FOR MATCH SCORE
  const renderMatchScore = (p) => {
    const score = p.row.match_score;
    if (score === undefined || score === null) return renderCenterCell('--');
    
    return renderCenterCell(
      score >= 90 ? (
        <Tooltip title={`${score}% Match`} placement="top" arrow>
          <CheckCircleIcon color="success" sx={{ fontSize: 22 }} />
        </Tooltip>
      ) : (
        <Chip size="small" label={`${score}%`} color="warning" sx={{ height: 20, fontSize: '0.7rem', fontWeight: 'bold' }} />
      )
    );
  };

  // Safely truncate address and reveal via Tooltip
  const renderAddress = (p) => {
    const addr = p.row.address || '--';
    if (addr.length <= 15) return renderCenterCell(<Typography variant="body2">{addr}</Typography>);
    return renderCenterCell(
      <Tooltip title={addr} arrow placement="top">
        <Typography variant="body2" sx={{ cursor: 'pointer', borderBottom: '1px dashed #ccc' }}>
          {addr.substring(0, 15)}...
        </Typography>
      </Tooltip>
    );
  };

  const sharedCols = [
    { field: 'emp_details', headerName: 'Employee Name & ID', width: 220, valueGetter: (p, r) => `${r.employee_name} ${r.emp_id}`, renderCell: (p) => renderStackedCell(p.row.employee_name, p.row.emp_id) },
    { field: 'dept_details', headerName: 'Department & Designation', width: 220, valueGetter: (p, r) => `${r.department} ${r.designation}`, renderCell: (p) => renderStackedCell(p.row.department, p.row.designation) },
  ];

  const viewAction = {
    field: 'actions', headerName: 'View', width: 70, sortable: false, renderCell: (p) => renderCenterCell(
      <IconButton size="small" color="primary" onClick={() => window.open(`${baseUrl}/employees/documents/${p.row.document_id}/download`, '_blank')}>
        <VisibilityIcon fontSize="small" />
      </IconButton>
    )
  };

  // Rebalanced widths and added the dedicated match_score column
  const panCols = [
    ...sharedCols,
    { field: 'name_on_card', headerName: 'Name on PAN', flex: 1, minWidth: 160 },
    { field: 'match_score', headerName: 'Match', width: 70, renderCell: renderMatchScore },
    { field: 'father_name', headerName: 'Father Name', width: 160 },
    { field: 'dob', headerName: 'DOB', width: 110 },
    { field: 'pan_number', headerName: 'PAN Number', width: 130, renderCell: (p) => renderCenterCell(<Typography variant="body2" sx={{ fontWeight: 'bold' }}>{p.row.pan_number}</Typography>) },
    { field: 'upload_date', headerName: 'Upload Date', width: 110, renderCell: (p) => renderCenterCell(<Typography variant="body2">{p.row.upload_date ? new Date(p.row.upload_date).toLocaleDateString() : '--'}</Typography>) },
    viewAction
  ];

  const aadhaarCols = [
    ...sharedCols,
    { field: 'name_on_card', headerName: 'Name on Aadhaar', flex: 1, minWidth: 160 },
    { field: 'match_score', headerName: 'Match', width: 70, renderCell: renderMatchScore },
    { field: 'dob', headerName: 'DOB', width: 110 },
    { field: 'aadhaar_number', headerName: 'Aadhaar Number', width: 140, renderCell: (p) => renderCenterCell(<Typography variant="body2" sx={{ fontWeight: 'bold' }}>{p.row.aadhaar_number}</Typography>) },
    { field: 'address', headerName: 'Address', width: 120, renderCell: renderAddress }, // Minimized as requested
    { field: 'upload_date', headerName: 'Upload Date', width: 110, renderCell: (p) => renderCenterCell(<Typography variant="body2">{p.row.upload_date ? new Date(p.row.upload_date).toLocaleDateString() : '--'}</Typography>) },
    viewAction
  ];

  return (
    <Box sx={{ height: '100%', p: 2 }}>
      <DataGrid
        loading={loading}
        rows={data}
        columns={activeMenu === 'pan_card' ? panCols : aadhaarCols}
        getRowId={(row) => row.id}
        slots={{ toolbar: GridToolbar }}
        slotProps={{ toolbar: { showQuickFilter: true, quickFilterProps: { debounceMs: 500 } } }}
        initialState={{ pagination: { paginationModel: { pageSize: 20 } } }}
        pageSizeOptions={[10, 20, 50, 100]}
        disableRowSelectionOnClick
        sx={{
          border: 'none',
          '& .MuiDataGrid-columnHeaders': { bgcolor: '#f5f5f5', fontWeight: 'bold' },
          '& .MuiDataGrid-columnHeader:first-of-type, & .MuiDataGrid-cell:first-of-type': { position: 'sticky', left: 0, zIndex: 3, backgroundColor: '#fff', boxShadow: '2px 0 4px -2px rgba(0,0,0,0.2)' }
        }}
      />
    </Box>
  );
};

export default ScreenedDocumentsTab;